/**
 * DevPanel — lightweight DOM-based debug overlay.
 *
 * Renders in the top-left corner of the game canvas.
 * Hidden by default. Click the toggle icon (top-right) to show/hide.
 * Sections can be added/updated by key.
 */

const PANEL_STYLE = `
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.75);
  color: #e2e8f0;
  font-family: monospace;
  font-size: 10px;
  padding: 6px 10px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 9999;
  max-width: 220px;
  line-height: 1.5;
  display: none;
`;

const TOGGLE_STYLE = `
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  background: rgba(0, 0, 0, 0.5);
  color: #94a3b8;
  font-family: monospace;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  border-radius: 3px;
  cursor: pointer;
  z-index: 9999;
  user-select: none;
`;

export class DevPanel {
  private el: HTMLDivElement;
  private toggleEl: HTMLDivElement;
  private sections = new Map<string, string>();
  private visible = false;

  constructor() {
    const container = document.getElementById("game") ?? document.body;
    container.style.position = "relative";

    // Panel content (hidden by default)
    this.el = document.createElement("div");
    this.el.style.cssText = PANEL_STYLE;
    container.appendChild(this.el);

    // Toggle icon (top-right)
    this.toggleEl = document.createElement("div");
    this.toggleEl.style.cssText = TOGGLE_STYLE;
    this.toggleEl.textContent = "D";
    this.toggleEl.title = "Toggle debug panel";
    this.toggleEl.addEventListener("click", () => this.toggle());
    container.appendChild(this.toggleEl);
  }

  private toggle(): void {
    this.visible = !this.visible;
    this.el.style.display = this.visible ? "block" : "none";
    this.toggleEl.style.color = this.visible ? "#e2e8f0" : "#94a3b8";
  }

  /** Set or update a named section. Content is HTML. */
  set(key: string, content: string): void {
    this.sections.set(key, content);
    this.render();
  }

  /** Remove a section. */
  remove(key: string): void {
    this.sections.delete(key);
    this.render();
  }

  /** Clear all sections. */
  clear(): void {
    this.sections.clear();
    this.render();
  }

  private render(): void {
    const parts: string[] = [];
    for (const [key, content] of this.sections) {
      parts.push(`<div style="margin-bottom:4px"><span style="color:#94a3b8">${key}:</span> ${content}</div>`);
    }
    this.el.innerHTML = parts.join("");
  }

  destroy(): void {
    this.el.remove();
    this.toggleEl.remove();
  }
}
